<template>
  <div class="review">
    <div class="left-container">
      <ui-button
        class="review__actor"
        :variant="checkBtnVariant(ReviewType.like)"
        :colorMode="UiButtonColorMode.NEUTRAL"
        iconOnly
        @clicked="sendReaction(ReviewType.like)"
      >
        <template #icon>
          <IconBase
            name="thumb_up"
            :color="checkIconColor(ReviewType.like)"
          />
        </template>
      </ui-button>

      <ui-button
        class="review__actor"
        :variant="checkBtnVariant(ReviewType.dislike)"
        :colorMode="UiButtonColorMode.NEUTRAL"
        iconOnly
        @clicked="sendReaction(ReviewType.dislike)"
      >
        <template #icon>
          <IconBase
            name="thumb_down"
            :color="checkIconColor(ReviewType.dislike)"
          />
        </template>
      </ui-button>

      <Share />
    </div>

    <span
      class="review__error-link"
      role="button"
      @click="sendReaction(ReviewType.error)"
    >
      Заметили ошибку?
    </span>

    <ui-modal
      :is-shown="isFormVisible"
      size="s"
      title="Поделитесь вашим мнением"
      is-footer-hidden
      @close="hideForm"
    >
      <template #content>
        <div class="review-modal-subtitle">
          Расскажите, что&nbsp;понравилось или&nbsp;не&nbsp;понравилось, где&nbsp;заметили ошибку или&nbsp;неточность
        </div>

        <ui-textarea
          ref="textComponentRef"
          class="review-modal-textfield"
          :value="text"
          placeholder="Ваш отзыв"
          autofocus
          :max-length="maxLength"
          :rows="3"
          @inputValue="enterText"
        />

        <div class="modal__buttons">
          <ui-button
            text="Отправить"
            :disabled="!isFormValid"
            @click.stop="sendReviewText"
          />
        </div>
      </template>
    </ui-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { UiModal } from '@experience/core-modal-ui';
  import { UiTextarea } from '@experience/core-textarea-ui';
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import IconBase from '@/shared/ui/iconBase/IconBase.vue';
  import Share from './ShareContainer.vue';

  enum ReviewType {
    unset = -1,
    like,
    dislike,
    error,
  }

  const props = defineProps<{
    review: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'send-reaction', type: ReviewType, sogu: boolean, save: boolean): void;
    (e: 'send-review', type: ReviewType, text: string): void;
  }>();

  const formReviewType = ref(ReviewType.unset);
  const text = ref('');
  const textComponentRef = ref(null);

  const isFormVisible = computed(() => formReviewType.value !== ReviewType.unset);
  const isFormValid = computed(() => text.value.length > 0 && text.value.length <= 1000);
  const maxLength = computed(() => (text.value.length < 990 ? undefined : 1000));

  const checkBtnVariant = (expected: ReviewType) => {
    return props.review === expected ? UiButtonVariant.PRIMARY : UiButtonVariant.SECONDARY;
  };

  const checkIconColor = (expected: ReviewType) => {
    return props.review === expected ? '--alias-color-icon-inverse' : '--alias-color-icon-default';
  };

  const sendReaction = (reviewType: ReviewType) => {
    switch (reviewType) {
      case ReviewType.like:
        reviewType = props.review === ReviewType.like ? ReviewType.unset : ReviewType.like;
        break;
      case ReviewType.dislike:
        reviewType = props.review === ReviewType.dislike ? ReviewType.unset : ReviewType.dislike;
        break;
      default:
        break;
    }

    if (reviewType > ReviewType.unset) {
      formReviewType.value = reviewType;
    }

    emit(
      'send-reaction',
      reviewType,
      reviewType !== ReviewType.unset, // send sogu event
      reviewType !== ReviewType.error, // save in localStorage
    );
  };

  const enterText = (value: string) => {
    text.value = value.length < 1000 ? value : value.slice(0, 1000);
  };

  const hideForm = () => {
    formReviewType.value = ReviewType.unset;
    text.value = '';
  };

  const sendReviewText = () => {
    const reviewType = formReviewType.value;
    formReviewType.value = ReviewType.unset;
    emit('send-review', reviewType, text.value);
    text.value = '';
  };
</script>
<style lang="scss" scoped>
  .review {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 805px;
    margin: var(--alias-space-48, 48px) 0;

    @include tablet {
      margin: var(--alias-space-32, 32px) 0;
    }

    @include mobile {
      flex-direction: column;
      gap: 16px;
      align-items: flex-start;
      max-width: auto;
    }
  }

  .left-container {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .review__actor {
    flex-grow: 0;
    flex-shrink: 0;
  }

  .review__spacer {
    flex-grow: 1;
    flex-shrink: 1;
  }

  .review__error-link {
    color: var(--alias-color-link-default);
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }
  }

  .review-modal-subtitle {
    @include base-300-300-regular;
  }

  .review-modal-textfield {
    margin: var(--alias-space-16) auto var(--alias-space-8);
  }

  .modal__buttons {
    margin-top: 24px;
  }
</style>
